<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card outlined class="mt-2">
          <v-card-title class="pb-1 text-button">
            Сводная таблица аналогов
          </v-card-title>
          <v-card-text class="pt-2">
            <v-row dense>
              <v-col cols="12" md="9">
                <v-simple-table dense class="agency-status-table">
                  <template #default>
                    <thead>
                      <tr>
                        <th class="text-center">
                          Выбран в качестве ТА/ЛП у эксперта 1
                        </th>
                        <th>МНН: лекарственная форма</th>
                        <th class="text-center">Итоговый выбор</th>
                        <th class="text-center">
                          Выбран в качестве ТА/ЛП у эксперта 2
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in agencyStatusRows" :key="`status-row-${row.index}`">
                        <td class="text-center">
                          <v-btn
                            icon
                            x-small
                            :disabled="row.leftStatus === 'missing'"
                            @click="selectStatusRow(row.index, 1)"
                          >
                            <v-icon :color="statusIconColor(row.leftStatus)">
                              {{ statusIconName(row.leftStatus) }}
                            </v-icon>
                          </v-btn>
                        </td>
                        <td>
                          <div
                            class="agency-row-label"
                            :class="{ 'agency-row-label--disabled': row.resultStatus === 'missing' }"
                            @click="row.resultStatus !== 'missing' && selectStatusRow(row.index, 0)"
                            role="button"
                          >
                            {{ row.label }}
                          </div>
                        </td>
                        <td class="text-center">
                          <v-checkbox
                            dense
                            hide-details
                            :input-value="row.resultStatus === 'selected'"
                            :disabled="row.resultStatus === 'missing'"
                            @change="onResultCheckboxChange(row.index, $event)"
                          />
                        </td>
                        <td class="text-center">
                          <v-btn
                            icon
                            x-small
                            :disabled="row.rightStatus === 'missing'"
                            @click="selectStatusRow(row.index, 2)"
                          >
                            <v-icon :color="statusIconColor(row.rightStatus)">
                              {{ statusIconName(row.rightStatus) }}
                            </v-icon>
                          </v-btn>
                        </td>
                      </tr>
                      <tr v-if="!agencyStatusRows.length">
                        <td colspan="4" class="text-center text--disabled py-2">
                          Нет данных для выбранного агентства
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
              <v-col cols="12" md="3">
                <div class="agency-legend">
                  <div class="font-weight-medium mb-1">Обозначение:</div>
                  <div class="mb-1">
                    <v-icon small color="error">mdi-checkbox-blank-circle</v-icon>
                    ЛП был внесен в ПИКО, но не выбран
                  </div>
                  <div class="mb-1">
                    <v-icon small color="success">mdi-checkbox-marked-circle</v-icon>
                    ЛП был внесен и выбран как аналог
                  </div>
                  <div class="mb-1">
                    <v-icon small color="grey lighten-1">mdi-checkbox-blank-circle-outline</v-icon>
                    ЛП не был внесен в ПИКО
                  </div>
                  <div>
                    <v-icon small color="primary">mdi-checkbox-blank-outline</v-icon>
                    Итоговый выбор аналога для сводной версии
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row dense class="mt-2">
              <v-col cols="12" md="6">
                <div class="agency-hint">
                  Данная фраза сохраняется и обновляется в таблицу ниже
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="agency-hint">
                  При клике на ЛП в сводной версии раскрывается паспорт ЛП/ТА ЛП
                </div>
              </v-col>
            </v-row>
            <v-textarea
              v-model="agencyGeneralComment"
              class="mt-2"
              outlined
              auto-grow
              rows="2"
              label="Общий комментарий (аналоги изменить на ТА ЛП)"
              hide-details="auto"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="text-subtitle-2 font-weight-bold mt-2 mb-2">
          Показания препарата
        </div>
        <v-alert v-if="about.error" type="error" outlined dense>
          {{ about.error }}
        </v-alert>
        <v-card v-else :loading="about.loading" flat>
          <v-textarea
            v-if="about.data.about_ready && !compareIndications.length"
            :value="about.data.condition"
            readonly
            auto-grow
            rows="2"
          />
          <div v-if="compareIndications.length" class="mt-2">
            <v-tabs
              v-model="activeIndication"
              show-arrows
              class="compare-indication-tabs"
              @change="onIndicationChange"
            >
              <v-tab
                v-for="(ind, indIndex) in compareIndications"
                :key="indIndex"
                class="compare-indication-tab"
              >
                {{ indicationTabLabel(ind, indIndex) }}
              </v-tab>
            </v-tabs>
            <div class="compare-indication-text mt-2">
              {{ activeIndicationText || 'Показание не заполнено' }}
            </div>
          </div>
          <v-alert
            v-else-if="!about.data.about_ready"
            type="warning"
            outlined
            dense
          >
            Ожидание сводной версии «Информации о препарате»
          </v-alert>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-tabs v-model="activeTab" @change="doBlur()">
          <v-tab
            v-for="(tabName, tabIndex) in agencyTabNames"
            :key="tabIndex"
            class="font-weight-bold"
          >
            {{ tabName.name }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab">
          <v-tab-item
            v-for="(tabName, tabIndex) in agencyTabNames"
            :key="tabIndex"
          >
            <v-card shaped outlined class="mt-2">
              <v-card-title class="pb-0 text-button"
                >Выбор заключения</v-card-title
              >
              <v-card-text>
                <CompareRow
                  :root="conclusionsRoot"
                  :root-index="activeTab.toString()"
                  tag="data"
                  hideUseBtn
                  editable
                  v-slot="{ value, position }"
                >
                  <v-select
                    v-model="selectedData[activeTab][position]"
                    :items="getTitles(value)"
                    item-text="name"
                    item-value="index"
                    hide-details
                  >
                    <template #append-outer v-if="getTitles(value).length > 1">
                      <v-chip small class="my-0">
                        {{ selectedData[activeTab][position] + 1 }} из
                        {{ getTitles(value).length }}
                      </v-chip>
                    </template>
                  </v-select>
                </CompareRow>
                <CompareRow
                  :root="conclusionItemRoot"
                  :root-index="String(selectedData[activeTab][0])"
                  append
                  @append="updateAppend"
                  remove
                  @remove="requestRemoveComment"
                  v-slot="{ value }"
                >
                  <v-alert
                    v-if="!value"
                    type="warning"
                    border="top"
                    colored-border
                    elevation="1"
                    >Нет данных</v-alert
                  >
                </CompareRow>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>

        <v-card outlined class="mt-2">
          <v-card-title>Оригинал</v-card-title>
          <v-card-text class="pb-0">
            <CompareRow
              :root="conclusionItemRoot"
              :root-index="String(selectedData[activeTab][0])"
              tag="original"
              v-slot="{ value, change, data }"
            >
              <AutoGrowTextEditor
                v-if="data"
                @change="change"
                @input="change"
                :value="value"
              />
            </CompareRow>
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-2">
          <v-card-title>Перевод</v-card-title>
          <v-card-text class="pb-0">
            <CompareRow
              :root="conclusionItemRoot"
              :root-index="String(selectedData[activeTab][0])"
              tag="translate"
              v-slot="{ value, change, data }"
            >
              <AutoGrowTextEditor
                v-if="data"
                @change="change"
                @input="change"
                :value="value"
              />
            </CompareRow>
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-2">
          <v-card-title>Ссылка</v-card-title>
          <v-card-text class="pb-0">
            <CompareRow
              :root="conclusionItemRoot"
              :root-index="String(selectedData[activeTab][0])"
              tag="link"
              v-slot="{ value, change, data, isResult }"
            >
              <ArrangeLink
                v-if="data"
                :value="value"
                @input="change"
                :no-validate="!isResult"
              />
            </CompareRow>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="removeDialog" max-width="560">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Комментарий при удалении
        </v-card-title>
        <v-card-text>
          <div class="mb-2">
            Укажите причину удаления заключения из сводной версии.
          </div>
          <v-textarea
            v-model="removeComment"
            outlined
            auto-grow
            rows="2"
            hide-details="auto"
            placeholder="Например: удалено как нерелевантное для итогового выбора"
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="cancelRemove">Отмена</v-btn>
          <v-btn color="primary" :disabled="!removeComment.trim()" @click="confirmRemove">
            Подтвердить удаление
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { objectByPath } from '@/lib/objects'
import {
  DEFAULT_AGENCIES_LIST,
  indicationTabLabel,
  normalizeAgenciesExpertise,
} from './const'
import CompareRow from '@/components/compare/CompareRow'
import { SUB_EX_ABOUT } from '@/store/const/expertise'
import ArrangeLink from '@/components/editors/ArrangeLink'
import AutoGrowTextEditor from '@/components/editors/AutoGrowTextEditor'

export default {
  name: 'Compare-Agencies',
  props: {
    dataReady: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CompareRow,
    AutoGrowTextEditor,
    ArrangeLink,
  },
  data: () => ({
    activeIndication: 0,
    activeTab: 0,
    selectedData: [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
    ],
    about: {
      loading: false,
      error: '',
      data: null,
    },
    removeDialog: false,
    removeComment: '',
    pendingRemove: null,
  }),
  computed: {
    ...mapGetters([
      'compare_data',
      'compare_expertise_0_Data',
      'compare_expertise_1_Data',
    ]),
    centerIndications() {
      return (
        normalizeAgenciesExpertise(this.compare_data || {}).indications || []
      )
    },
    leftIndications() {
      return (
        normalizeAgenciesExpertise(this.compare_expertise_0_Data || {})
          .indications || []
      )
    },
    rightIndications() {
      return (
        normalizeAgenciesExpertise(this.compare_expertise_1_Data || {})
          .indications || []
      )
    },
    compareIndications() {
      const len = Math.max(
        this.centerIndications.length,
        this.leftIndications.length,
        this.rightIndications.length
      )
      return Array.from({ length: len }, (_, index) => {
        const center = this.centerIndications[index]
        const left = this.leftIndications[index]
        const right = this.rightIndications[index]
        return (
          center ||
          left ||
          right || {
            text: '',
            agencies: DEFAULT_AGENCIES_LIST(),
          }
        )
      })
    },
    agencyTabNames() {
      return (
        this.compareIndications[this.activeIndication]?.agencies ||
        DEFAULT_AGENCIES_LIST()
      )
    },
    conclusionsRoot() {
      return `indications[${this.activeIndication}].agencies`
    },
    conclusionItemRoot() {
      return `indications[${this.activeIndication}].agencies[${this.activeTab}].data`
    },
    removeCommentsRoot() {
      return `indications[${this.activeIndication}].agencies[${this.activeTab}].removeComments`
    },
    agencyGeneralComment: {
      get() {
        return objectByPath(
          this.compare_data,
          `indications[${this.activeIndication}].agencies[${this.activeTab}].generalComment`
        ) || ''
      },
      set(value) {
        this.COMPARE_PART_DATA({
          root: `indications[${this.activeIndication}].agencies[${this.activeTab}]`,
          tag: 'generalComment',
          value: String(value || ''),
        })
      },
    },
    currentAgencyData() {
      const ind = this.activeIndication
      const tab = this.activeTab
      return {
        result:
          this.centerIndications[ind]?.agencies?.[tab]?.data || [],
        left:
          this.leftIndications[ind]?.agencies?.[tab]?.data || [],
        right:
          this.rightIndications[ind]?.agencies?.[tab]?.data || [],
      }
    },
    agencyStatusRows() {
      const { result, left, right } = this.currentAgencyData
      const maxLen = Math.max(result.length, left.length, right.length)
      const selectedResult = this.selectedData?.[this.activeTab]?.[0] ?? 0
      const selectedLeft = this.selectedData?.[this.activeTab]?.[1] ?? 0
      const selectedRight = this.selectedData?.[this.activeTab]?.[2] ?? 0
      return Array.from({ length: maxLen }, (_, index) => {
        const label = this.getConclusionLabel(index, result, left, right)
        return {
          index,
          label,
          leftStatus: this.resolveStatus(index, left.length, selectedLeft),
          resultStatus: this.resolveStatus(index, result.length, selectedResult),
          rightStatus: this.resolveStatus(index, right.length, selectedRight),
        }
      })
    },
    activeIndicationText() {
      const index = this.activeIndication
      return (
        this.centerIndications[index]?.text ||
        this.leftIndications[index]?.text ||
        this.rightIndications[index]?.text ||
        ''
      )
    },
  },
  created() {
    this.loadAbout()
  },
  methods: {
    indicationTabLabel,
    ...mapActions(['GET_PLP_REPORT']),
    ...mapMutations(['COMPARE_PART_DATA']),
    doBlur() {},
    onResultCheckboxChange(rowIndex, val) {
      if (val) this.selectStatusRow(rowIndex, 0)
    },
    onIndicationChange() {
      this.activeTab = 0
      this.selectedData = [
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0],
      ]
    },
    updateAppend(object, index) {
      this.selectedData[this.activeTab][0] = index
    },
    requestRemoveComment(object, index, count) {
      this.pendingRemove = { object, index, count }
      this.removeComment = ''
      this.removeDialog = true
    },
    restoreRemovedConclusion() {
      const removed = this.pendingRemove?.object?.[0]
      const index = this.pendingRemove?.index
      if (removed == null || !Number.isInteger(index)) return
      const current = objectByPath(this.compare_data, this.conclusionItemRoot) || []
      const restored = [...current]
      restored.splice(index, 0, removed)
      this.COMPARE_PART_DATA({ root: this.conclusionItemRoot, value: restored })
      this.selectedData[this.activeTab][0] = index
    },
    cancelRemove() {
      this.restoreRemovedConclusion()
      this.pendingRemove = null
      this.removeDialog = false
      this.removeComment = ''
    },
    confirmRemove() {
      if (!this.removeComment.trim()) return
      const { index, count } = this.pendingRemove || {}
      const comments = objectByPath(this.compare_data, this.removeCommentsRoot) || []
      const nextComments = [...comments, {
        indication: this.activeIndication,
        agency: this.activeTab,
        index,
        comment: this.removeComment.trim(),
        createdAt: new Date().toISOString(),
      }]
      this.COMPARE_PART_DATA({
        root: this.removeCommentsRoot,
        value: nextComments,
      })
      if (count === index) this.selectedData[this.activeTab][0] = count - 1
      this.pendingRemove = null
      this.removeDialog = false
      this.removeComment = ''
    },
    getTitles(value) {
      return (
        value?.reduce((items, item, index) => {
          items.push({ index: index, name: `Заключение ${index + 1}` })
          return items
        }, []) || []
      )
    },
    resolveStatus(index, length, selectedIndex) {
      if (index >= length) return 'missing'
      return index === selectedIndex ? 'selected' : 'present'
    },
    selectStatusRow(index, position) {
      const { result, left, right } = this.currentAgencyData
      const lengths = [result.length, left.length, right.length]
      const limit = lengths[position]
      if (!Number.isInteger(limit) || index < 0 || index >= limit) return
      this.$set(this.selectedData[this.activeTab], position, index)
    },
    getConclusionLabel(index, result, left, right) {
      const item = result[index] || left[index] || right[index]
      if (!item) return `Заключение ${index + 1}`
      const text =
        [
          item.title,
          item.name,
          item.original,
          item.translate,
          item.link,
        ].find(v => String(v || '').trim()) || ''
      const normalized = String(text).replace(/\s+/g, ' ').trim()
      if (!normalized) return `Заключение ${index + 1}`
      return normalized.length > 90
        ? `${normalized.slice(0, 90).trim()}...`
        : normalized
    },
    statusIconName(status) {
      if (status === 'selected') return 'mdi-checkbox-marked-circle'
      if (status === 'present') return 'mdi-checkbox-blank-circle'
      return 'mdi-checkbox-blank-circle-outline'
    },
    statusIconColor(status) {
      if (status === 'selected') return 'success'
      if (status === 'present') return 'error'
      return 'grey lighten-1'
    },
    async loadAbout() {
      this.about = { loading: true, data: null, error: '' }
      try {
        const { expertise } = await this.GET_PLP_REPORT([SUB_EX_ABOUT])
        const about_ready = !!expertise?.about
        const condition = expertise?.about?.condition || ''
        this.about.data = { about_ready, condition }
      } catch (err) {
        this.about.error = err.message + (err.error ? ' ' + err.error : '')
        throw err
      } finally {
        this.about.loading = false
      }
    },
  },
}
</script>

<style scoped>
.compare-indication-tabs >>> .v-tabs-bar {
  background: transparent;
}
.compare-indication-tab {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 700;
  font-size: 15px;
  min-width: 130px;
  min-height: 44px;
  padding: 0 18px;
}
.compare-indication-tabs >>> .v-tab {
  opacity: 0.9;
}
.compare-indication-tabs >>> .v-tab.v-tab--active {
  opacity: 1;
}
.compare-indication-text {
  white-space: pre-wrap;
  color: rgba(0, 0, 0, 0.7);
}
.agency-status-legend {
  font-size: 12px;
}
.agency-status-table th {
  font-size: 12px;
  white-space: normal;
}
.agency-legend {
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 12px;
  background: #fafafa;
}
.agency-hint {
  background: #4f4f4f;
  color: #fff;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 12px;
}
.agency-status-table th,
.agency-status-table td {
  border: 1px solid rgba(0, 0, 0, 0.35);
  padding: 4px 6px;
}
.agency-row-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
  cursor: pointer;
  text-decoration: underline;
  color: rgba(0, 0, 0, 0.75);
}
.agency-row-label--disabled {
  cursor: default;
  text-decoration: none;
  color: rgba(0, 0, 0, 0.25);
}
</style>
